import React, { useEffect, useState } from "react";
import { FiChevronDown, FiPhone, FiX } from "react-icons/fi";
import "./RequestCallModal.css";
import { createCallRequest, getCallRequestServices } from "../../../api/callRequests";

const RequestCallModal = ({ isOpen, onClose }) => {
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    message: "",
    serviceOptionId: "",
  });

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    const loadServices = async () => {
      setLoadingServices(true);
      setErrorMessage("");
      try {
        const data = await getCallRequestServices();
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setServices(list);
        setForm((prev) => ({
          ...prev,
          serviceOptionId: prev.serviceOptionId || String(list[0]?.id || ""),
        }));
      } catch (error) {
        if (!isMounted) return;
        setServices([]);
        setErrorMessage(error?.response?.data?.message || "Failed to load services.");
      } finally {
        if (isMounted) setLoadingServices(false);
      }
    };
    loadServices();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    if (!String(form.name || "").trim()) {
      setErrorMessage("Your name is required.");
      return;
    }
    if (!String(form.phoneNumber || "").trim()) {
      setErrorMessage("Phone number is required.");
      return;
    }
    if (!String(form.serviceOptionId || "").trim()) {
      setErrorMessage("Please choose a service.");
      return;
    }

    setSubmitting(true);
    try {
      await createCallRequest({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim() || null,
        phoneNumber: String(form.phoneNumber || "").trim(),
        message: String(form.message || "").trim() || null,
        serviceOptionId: Number(form.serviceOptionId),
      });
      setSuccessMessage("Request submitted successfully.");
      setForm((prev) => ({
        ...prev,
        name: "",
        email: "",
        phoneNumber: "",
        message: "",
      }));
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="requestCallModalOverlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="requestCallModal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Request a call"
      >
        <button type="button" className="requestCallModalClose" onClick={onClose} aria-label="Close modal">
          <FiX />
        </button>

        <div className="requestCallModalForm">
          <h2>Request a call</h2>
          <p>
            IRQGYP is a leading Iraqi company specialized in gypsum products and
            modern construction solutions.
          </p>

          <input
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <input
            type="email"
            placeholder="Email (optional)"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Phone number"
            value={form.phoneNumber}
            onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
          />
          <textarea
            placeholder="Message (optional)"
            rows="4"
            value={form.message}
            onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
          />

          <div className="requestCallModalSelect">
            <select
              value={form.serviceOptionId}
              onChange={(e) => setForm((p) => ({ ...p, serviceOptionId: e.target.value }))}
              disabled={loadingServices || !services.length}
              aria-label="Services you are interested in"
            >
              {services.length ? (
                services.map((service) => (
                  <option key={service.id} value={String(service.id)}>
                    {service.name}
                  </option>
                ))
              ) : (
                <option value="">{loadingServices ? "Loading services..." : "No services available"}</option>
              )}
            </select>
            <FiChevronDown />
          </div>

          {errorMessage ? <p className="requestCallModalMessage requestCallModalMessageError">{errorMessage}</p> : null}
          {successMessage ? <p className="requestCallModalMessage requestCallModalMessageSuccess">{successMessage}</p> : null}

          <button type="button" className="requestCallModalSubmit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting..." : "Request a call"}
          </button>

          <div className="requestCallModalPhoneRow">
            <FiPhone />
            <span>
              Or call us now: <strong>+964 750 202 0066</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestCallModal;
